/**
 * Sound — real recorded SFX (free-licensed: Mixkit / Pixabay / Kenney CC0),
 * decoded into WebAudio buffers on the first user gesture. If any file fails
 * to load, that slot silently falls back to a small procedural synth so the
 * game never goes mute.
 *
 * Slots: click, coin, boing, cluck, whoosh, driveby, horn, splat, lose,
 * jingle, ambient (looped street bed).
 */
const FILES = {
  click:   '/sounds/click.mp3',
  coin:    '/sounds/coin.mp3',
  boing:   '/sounds/boing.mp3',
  cluck:   '/sounds/cluck.mp3',
  horn:    '/sounds/horn.mp3',
  splat:   '/sounds/splat.mp3',
  lose:    '/sounds/lose.mp3',
  jingle:  '/sounds/jingle.mp3',
  ambient: '/sounds/ambient.mp3',
};

export default class Sound {
  constructor(bus) {
    this.ac = null;
    this.master = null;
    this.buffers = new Map();
    this.ambientOn = false;
    const ensure = () => this._ensure();
    window.addEventListener('pointerdown', ensure, { once: true });
    window.addEventListener('keydown', ensure, { once: true });

    bus.on('sfx:click', () => this.play('click', { gain: 0.35 }));
    bus.on('chicken:hop', () => {
      this.play('boing', { gain: 0.5, jitter: 0.07 });
      if (Math.random() < 0.65) this.play('cluck', { gain: 0.32, jitter: 0.1, cutoff: 0.55 });
    });
    bus.on('chicken:landed', () => this.play('coin', { gain: 0.5, jitter: 0.04 }));
    bus.on('round:dead', () => {
      this.play('splat', { gain: 0.9 });
      this.play('lose', { gain: 0.55, delay: 0.4 });
    });
    bus.on('round:cashout', () => this.play('jingle', { gain: 0.7 }));
    // cars pass silently (the constant whoosh was grating); only the killer
    // car announces itself with a horn right before impact
    bus.on('sfx:horn', () => this.play('horn', { gain: 0.65, jitter: 0.05 }));
  }

  _ensure() {
    if (this.ac) return;
    this.ac = new (window.AudioContext || window.webkitAudioContext)();
    this.master = this.ac.createGain();
    this.master.gain.value = 0.6;
    this.master.connect(this.ac.destination);
    this._loadAll();
  }

  async _loadAll() {
    await Promise.allSettled(
      Object.entries(FILES).map(async ([name, url]) => {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${url}: ${res.status}`);
        const buf = await this.ac.decodeAudioData(await res.arrayBuffer());
        this.buffers.set(name, buf);
      })
    );
    this._startAmbient();
  }

  _startAmbient() {
    if (this.ambientOn || !this.buffers.has('ambient')) return;
    this.ambientOn = true;
    const src = this.ac.createBufferSource();
    src.buffer = this.buffers.get('ambient');
    src.loop = true;
    const g = this.ac.createGain();
    g.gain.value = 0;
    g.gain.linearRampToValueAtTime(0.08, this.ac.currentTime + 2.5); // fade in
    src.connect(g).connect(this.master);
    src.start();
  }

  /**
   * @param {string} name slot
   * @param {{gain?:number, rate?:number, jitter?:number, delay?:number, cutoff?:number}} o
   *   jitter — random playback-rate variance; cutoff — stop after N seconds with a quick fade
   */
  play(name, o = {}) {
    if (!this.ac) return;
    const buf = this.buffers.get(name);
    if (!buf) return this._fallback(name);
    const t0 = this.ac.currentTime + (o.delay || 0);
    const src = this.ac.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = (o.rate || 1) * (1 + (o.jitter ? (Math.random() * 2 - 1) * o.jitter : 0));
    const g = this.ac.createGain();
    g.gain.setValueAtTime(o.gain ?? 0.5, t0);
    src.connect(g).connect(this.master);
    src.start(t0);
    if (o.cutoff && o.cutoff < buf.duration) {
      // fade out and stop early (stop() is only legal after start())
      g.gain.setValueAtTime(o.gain ?? 0.5, t0 + o.cutoff - 0.06);
      g.gain.linearRampToValueAtTime(0.0001, t0 + o.cutoff);
      src.stop(t0 + o.cutoff + 0.01);
    }
  }

  /* ------- tiny procedural fallbacks (used only if a file failed) ------- */

  _fallback(name) {
    const map = {
      click:  () => this._osc('triangle', 1900, 1400, 0.045, 0.12),
      coin:   () => { this._osc('sine', 1318, 1318, 0.18, 0.16); this._osc('sine', 1976, 1976, 0.24, 0.08, 0.02); },
      boing:  () => this._osc('square', 640, 430, 0.07, 0.1),
      cluck:  () => { this._osc('square', 640, 430, 0.07, 0.1); this._osc('square', 820, 520, 0.06, 0.08, 0.07); },
      horn:   () => { this._osc('square', 440, 440, 0.25, 0.2); this._osc('square', 554, 554, 0.25, 0.18); },
      splat:  () => { this._osc('sine', 170, 46, 0.24, 0.6); this._noise(0.22, 900, 140, 0.5); },
      lose:   () => { this._osc('sine', 392, 392, 0.2, 0.2); this._osc('sine', 330, 330, 0.25, 0.2, 0.22); this._osc('sine', 262, 262, 0.4, 0.2, 0.46); },
      jingle: () => [1046.5, 1318.5, 1568, 2093].forEach((f, i) => this._osc('sine', f, f, 0.22, 0.16, i * 0.09)),
    };
    map[name]?.();
  }

  _env(gainNode, t0, attack, decay, peak = 1) {
    const g = gainNode.gain;
    g.setValueAtTime(0.0001, t0);
    g.exponentialRampToValueAtTime(peak, t0 + attack);
    g.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
  }

  _osc(type, f0, f1, dur, peak = 0.5, delay = 0) {
    const t0 = this.ac.currentTime + delay;
    const osc = this.ac.createOscillator();
    const g = this.ac.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(f0, t0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
    this._env(g, t0, 0.008, dur, peak);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  }

  _noise(dur, filterFrom, filterTo, peak = 0.4, delay = 0, type = 'lowpass') {
    const t0 = this.ac.currentTime + delay;
    const len = Math.ceil(this.ac.sampleRate * dur);
    const buf = this.ac.createBuffer(1, len, this.ac.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ac.createBufferSource();
    src.buffer = buf;
    const filter = this.ac.createBiquadFilter();
    filter.type = type;
    filter.frequency.setValueAtTime(filterFrom, t0);
    filter.frequency.exponentialRampToValueAtTime(Math.max(40, filterTo), t0 + dur);
    const g = this.ac.createGain();
    this._env(g, t0, 0.01, dur, peak);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t0);
  }
}
